import {
  LightListingCard_ListingFragment,
  ListingCard_ListingFragment,
} from "@outschool/gql-frontend-generated";
import { gql } from "@outschool/ui-apollo";
import { SectionRibbon } from "@outschool/ui-components-shared";
import {
  LightActivityFragment,
  ListingCardFragment,
} from "@outschool/ui-components-website";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import * as React from "react";

import { useIsActivitySavedByCurrentUser } from "../../saving/graphql/hooks";
import { TileInfoName } from "./CardProps";
import { GroupClassCard } from "./GroupClassCard";
import { TutorCard } from "./TutorCard";
import TutorCardV2 from "./TutorCardV2";

export { MIN_CARD_HEIGHT } from "./constants";
export { ListingCardLoading } from "./ListingCardLoading";
export type { CardProps } from "./CardProps";
export * from "./CardProps";

type ListingCardProps = {
  carouselRowNum: number;
  children?: React.ReactChildren;
  disabled?: boolean;
  hideHeart?: boolean;
  hideTeacher?: boolean;
  isLastColumn?: boolean;
  listing: (ListingCard_ListingFragment | LightListingCard_ListingFragment) & {
    searchUid?: string;
  };
  linkTo?: string;
  openInNewTab?: boolean;
  showRating?: boolean;
  showSummary?: boolean;
  trackingLabel: string;
  trackingOrder: number;
  trackingProperties?: TrackingProperties;
  passSectionUid?: boolean;
  listingIndex?: number;
  variant?: string;
  isCrossSell?: boolean;
  tiles?: TileInfoName[];
  customOnClick?: (e: any) => void;
  shareButtonFunction?: () => void;
  isTutoringCLP?: boolean;
  isCurrentlyViewing?: boolean;
  overrideShowCredits?: boolean;
  columnWidth?: number;
};

function isLight(
  listing: ListingCard_ListingFragment | LightListingCard_ListingFragment,
): listing is LightListingCard_ListingFragment {
  return (listing as ListingCard_ListingFragment).nextSection == undefined;
}

export function trackingObject({
  activityUid,
  carouselRowNum,
  sectionCount,
  sizeMax,
  trackingLabel,
  trackingOrder,
  insertionId,
  trackingProperties,
  sharedProperties = {},
}: {
  activityUid: string;
  carouselRowNum: number;
  sectionCount?: number;
  sizeMax?: number | null;
  trackingLabel?: string;
  trackingOrder?: number;
  insertionId?: string | null | undefined;
  trackingProperties?: Record<any, any>;
  sharedProperties?: Record<any, any>;
}) {
  return {
    trackingName: "Activity Impression",
    sharedProperties: {
      activity_uid: activityUid,
      page: trackingLabel,
      order: trackingOrder,
      section_count: sectionCount ?? 0,
      size_max: sizeMax,
      carousel_row_num: carouselRowNum,
      insertion_id: insertionId ?? null,
      ...sharedProperties,
      ...trackingProperties,
    },
    trackingTouchEventName: "Activity Impression Click",
    trackingViewEventName: "Activity Impression",
    trackingUniqueId: activityUid,
  };
}

export function ListingCard({
  listing,
  trackingOrder,
  trackingLabel,
  carouselRowNum,
  variant,
  isLastColumn = false,
  disabled = false,
  trackingProperties = {},
  hideHeart = false,
  openInNewTab = false,
  listingIndex = -1,
  linkTo,
  isCrossSell = false,
  tiles = [
    TileInfoName.Age,
    TileInfoName.CapacityOrDuration,
    TileInfoName.Price,
  ],
  customOnClick,
  shareButtonFunction,
  isTutoringCLP = false,
  isCurrentlyViewing = false,
  overrideShowCredits = false,
  columnWidth,
}: ListingCardProps) {
  const showEducatorTile = isTutoringCLP;

  let activity: ListingCard_ListingFragment["activity"], leader, sectionCount;

  if (isLight(listing)) {
    const { activity: activityLight, leader: leaderLight } =
      listing as LightListingCard_ListingFragment;
    activity = activityLight;
    leader = leaderLight;
  } else {
    const {
      activity: a,
      leader: l,
      sectionCount: sc,
    } = listing as ListingCard_ListingFragment;
    activity = a;
    leader = l;
    sectionCount = sc;
  }

  const isSavedByUser = useIsActivitySavedByCurrentUser(activity);
  const useTutorCard =
    activity.size_max === 1 && !isCrossSell && !activity.isSelfPaced;

  const educatorTileTrackingProperties = trackingObject({
    activityUid: activity?.uid,
    carouselRowNum,
    trackingLabel,
    sectionCount,
    sizeMax: activity?.size_max,
    trackingOrder,
    insertionId: listing.insertionId,
    trackingProperties,
  });

  if (showEducatorTile) {
    return (
      <TutorCardV2
        listing={listing}
        trackingProperties={educatorTileTrackingProperties}
      />
    );
  }

  const Card = useTutorCard ? TutorCard : GroupClassCard;

  return (
    <Card
      activity={activity}
      disabled={disabled}
      hideHeart={hideHeart}
      isLastColumn={isLastColumn}
      isSavedByUser={isSavedByUser}
      leader={leader}
      linkTo={linkTo}
      trackingProperties={educatorTileTrackingProperties}
      openInNewTab={openInNewTab}
      listingIndex={listingIndex}
      variant={variant}
      insertionId={listing.insertionId}
      isCrossSell={isCrossSell}
      tiles={tiles}
      customOnClick={customOnClick}
      shareButtonFunction={shareButtonFunction}
      isCurrentlyViewing={isCurrentlyViewing}
      overrideShowCredits={overrideShowCredits}
      columnWidth={columnWidth}
    />
  );
}

ListingCard.fragments = {
  listing: gql`
    ${ListingCardFragment}
    ${LightActivityFragment}
    ${SectionRibbon.fragments.section}
    ${SectionRibbon.fragments.activity}
  `,
};
